// Accounts in a department that are not members of it (#2307).
//
// Two kinds of account sign into a department without being on its roster:
//
//   - the platform operator, one reserved `ember-operator` uid per project
//     (docs/architecture/operator-identity-plan.md), and
//   - a regional hub user signed into one of the hub's departments, one account
//     per hub user per department, under a `hub:<hubProjectId>:<hubUid>` uid.
//
// Neither is a member: they must stay out of rosters, reports, payroll,
// approver and assignee lists, reminders and roll-ups. `isHiddenAccount` is
// the one test for that, keyed on the uid. Only the api creates accounts and a
// uid can't be changed, so nobody can write their way onto or off the roster
// the way a Firestore field would allow.
//
// It answers "is this a member?", never "may this caller do something": a hub
// account is hidden but has none of the operator's powers. Operator-only gates
// keep their own check.
//
// `superadmin === true` is still honoured for now: the local emulator seeds keep
// a password superadmin under another uid, and #2181 retires the field.

const OPERATOR_UID = 'ember-operator';
const HUB_ACCOUNT_UID_PREFIX = 'hub:';

const isOperatorUid = uid => uid === OPERATOR_UID;

const isHubAccountUid = uid => typeof uid === 'string' && uid.startsWith(HUB_ACCOUNT_UID_PREFIX);

// The department account for one hub user. A hub project id can't contain a
// colon (GCP ids are lowercase letters, digits and hyphens), so the first colon
// after the prefix always ends it, whatever the hub uid holds.
const hubAccountUid = (hubProjectId, hubUid) => {
    if (typeof hubProjectId !== 'string' || !/^[a-z0-9-]+$/.test(hubProjectId)) {
        throw new Error('hubAccountUid: invalid hub project id');
    }
    if (typeof hubUid !== 'string' || !hubUid || hubUid.includes('/')) {
        throw new Error('hubAccountUid: invalid hub uid');
    }
    return `${HUB_ACCOUNT_UID_PREFIX}${hubProjectId}:${hubUid}`;
};

const parseHubAccountUid = uid => {
    if (!isHubAccountUid(uid)) return null;
    const rest = uid.slice(HUB_ACCOUNT_UID_PREFIX.length);
    const split = rest.indexOf(':');
    if (split <= 0 || split === rest.length - 1) return null;
    return {hubProjectId: rest.slice(0, split), hubUid: rest.slice(split + 1)};
};

const isReservedUid = uid => isOperatorUid(uid) || isHubAccountUid(uid);

// Reads the record's own `uid`/`id`; pass `uid` as well when the record is a
// bare `doc.data()` whose id is only on the snapshot.
const isHiddenAccount = (user, uid) =>
    [uid, user?.uid, user?.id].some(isReservedUid) || user?.superadmin === true;

module.exports = {
    OPERATOR_UID,
    HUB_ACCOUNT_UID_PREFIX,
    isOperatorUid,
    isHubAccountUid,
    hubAccountUid,
    parseHubAccountUid,
    isHiddenAccount
};
